// One course, as its learner sees it: the cover and what the course is, how far
// they are through it and where to carry on, then every module — each with its
// own picture — and the lessons inside, open or shut and why.
//
// A lesson staff have not published yet (blocked_reason "unpublished") is not
// shut in the sense the others are: nothing the learner does will open it. So
// it is shown as "coming soon" rather than locked, and a course with no
// published lesson at all says so at the top instead of showing 0% and no way
// in.

import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { ApiError, get } from '../api'
import type { CourseTree, LectureNode, SectionNode } from '../api'
import { useFormat, useT } from '../i18n'
import drafts from '../i18n/drafts'
import learn from '../i18n/learn'
import { Icon } from '../lib/ui'
import { CourseCover, LoadFailed, Loading } from './Courses'
import type { Failure } from './Courses'
import { reasonKey } from './Dashboard'
import '../styles/learn.css'
import '../styles/drafts.css'

/** GET /learn/courses/{id}: the tree, and how many of its lessons are drafts. */
type CourseView = CourseTree & { draft_lectures?: number }

/** A lesson staff have added but not published: coming soon, never openable. */
function isDraft(lecture: LectureNode): boolean {
  return lecture.blocked_reason === 'unpublished'
}

export function Course() {
  const t = useT(learn)
  const td = useT(drafts)
  const f = useFormat()
  const { courseId = '' } = useParams()
  const [tree, setTree] = useState<CourseView | null>(null)
  const [missing, setMissing] = useState(false)
  const [failure, setFailure] = useState<Failure | null>(null)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    const ac = new AbortController()
    setTree(null)
    setMissing(false)
    setFailure(null)
    get<CourseView>(`/learn/courses/${courseId}`, ac.signal)
      .then(setTree)
      .catch((err) => {
        if (ac.signal.aborted) return
        // Not enrolled, not published, or not here: the server says the same
        // thing for all three, and so does this page.
        if (err instanceof ApiError && err.status === 404) setMissing(true)
        else setFailure({ error: err })
      })
    return () => ac.abort()
  }, [courseId, attempt])

  if (missing) {
    return (
      <div className="empty">
        <h1>{t('courseNotFound')}</h1>
        <p>{t('courseNotFoundHint')}</p>
        <Link className="button" to="/courses">
          {t('myCourses')}
        </Link>
      </div>
    )
  }
  if (failure) return <LoadFailed error={failure.error} onRetry={() => setAttempt((n) => n + 1)} />
  if (!tree) return <Loading />

  const next = nextLesson(tree)
  const started = tree.sections.some((s) => s.lectures.some((l) => l.completed || l.progress > 0))
  // Section totals count published lessons only, so this is how many exist
  // for the learner at all.
  const published = tree.sections.reduce((n, s) => n + s.total, 0)
  const soon = tree.draft_lectures ?? tree.sections.reduce((n, s) => n + s.lectures.filter(isDraft).length, 0)

  return (
    <div>
      <header className="course-hero">
        <div className="course-hero__cover">
          <CourseCover fileId={tree.course.cover_file_id} title={tree.course.title} />
        </div>
        <div className="course-hero__body">
          <h1>{tree.course.title}</h1>
          {tree.course.description && <p className="course-hero__about">{tree.course.description}</p>}
          {published === 0 ? (
            <div className="course-soon">
              <Icon name="clock" size={20} />
              <div>
                <h2>{td('courseSoonTitle')}</h2>
                <p>{soon > 0 ? td('courseSoonCount', { n: soon }) : td('courseSoonEmpty')}</p>
              </div>
            </div>
          ) : (
            <>
              <div
                className="bar"
                role="progressbar"
                aria-label={t('progressOf', { title: tree.course.title })}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={tree.percent}
              >
                <div className="bar__fill" style={{ width: `${tree.percent}%` }} />
              </div>
              <p className="muted course-hero__figure">{t('percentDone', { percent: f.number(tree.percent) })}</p>
              {soon > 0 && (
                <p className="course-hero__more">
                  <Icon name="clock" size={16} />
                  {td('moreSoon', { n: soon })}
                </p>
              )}
              {next && (
                <div>
                  <Link className="button" to={`/lessons/${next.id}`}>
                    <Icon name="play" size={18} />
                    {started ? t('continueLesson', { title: next.title }) : t('startLesson', { title: next.title })}
                  </Link>
                </div>
              )}
            </>
          )}
        </div>
      </header>

      {/* A course with no module has nothing published either, and the hero
          above already says its lessons are on their way. */}
      {tree.sections.length > 0 && (
        <div className="tree">
          {tree.sections.map((section, i) => (
            <Module key={section.id} section={section} index={i} />
          ))}
        </div>
      )}
    </div>
  )
}

/** The first lesson in reading order that is open and not yet finished. */
function nextLesson(tree: CourseTree): LectureNode | null {
  for (const s of tree.sections) {
    if (s.blocked) continue
    for (const l of s.lectures) {
      if (!l.completed && !l.blocked) return l
    }
  }
  return null
}

function Module({ section, index }: { section: SectionNode; index: number }) {
  const t = useT(learn)
  const td = useT(drafts)
  const f = useFormat()
  // Every lesson of the module still a draft: the module itself is what is
  // coming, and a 0/0 count would say nothing.
  const allSoon = section.lectures.length > 0 && section.lectures.every(isDraft)
  const note =
    section.blocked_reason === 'schedule' && section.access_start_at
      ? t('opensOn', { date: f.dateTime(section.access_start_at) })
      : section.blocked
        ? t(reasonKey(section.blocked_reason))
        : allSoon
          ? td('moduleSoon')
          : ''
  const noteIcon = section.blocked && section.blocked_reason !== 'schedule' ? 'lock' : 'clock'

  return (
    <section className={section.blocked ? 'module module--shut' : 'module'} aria-labelledby={`module-${section.id}`}>
      <header className="module__head">
        {section.image_file_id && (
          <div className="module__picture">
            <CourseCover fileId={section.image_file_id} title={section.title} />
          </div>
        )}
        <div className="module__heading">
          <span className="module__number">{t('moduleNumber', { n: index + 1 })}</span>
          <span className="module__title" id={`module-${section.id}`}>
            {section.title}
          </span>
          {note && (
            <span className="module__note">
              <Icon name={noteIcon} size={14} />
              {note}
            </span>
          )}
        </div>
        {allSoon ? (
          <span className="soon-chip">{td('soon')}</span>
        ) : (
          <span className="module__count" title={t('lessonsDoneOf', { done: section.completed, total: section.total })}>
            {section.completed}/{section.total}
          </span>
        )}
      </header>
      {section.lectures.length === 0 ? (
        <p className="module__empty">{t('noLessonsYet')}</p>
      ) : (
        section.lectures.map((lecture) => <Row key={lecture.id} lecture={lecture} sectionShut={section.blocked} />)
      )}
    </section>
  )
}

function Row({ lecture, sectionShut }: { lecture: LectureNode; sectionShut: boolean }) {
  const t = useT(learn)
  const td = useT(drafts)

  // A draft says what it is in plain sight — on a phone there is no tooltip to
  // hover — and is neither a link nor a lock.
  if (isDraft(lecture)) {
    return (
      <div className="lesson-row lesson-row--shut lesson-row--soon">
        <span className="lesson-row__mark">
          <Icon name="clock" size={14} />
        </span>
        <span className="lesson-row__title">{lecture.title}</span>
        <span className="soon-chip">{td('soon')}</span>
      </div>
    )
  }

  const shut = lecture.blocked || sectionShut
  // A finished lesson's road is full whatever its last reported progress was.
  const pct = lecture.completed ? 100 : lecture.progress
  const state = lecture.completed ? t('lessonDone') : shut ? t(reasonKey(lecture.blocked_reason)) : ''

  const inner = (
    <>
      <span className="lesson-row__mark">
        <Glyph name={lecture.completed ? 'check' : shut ? 'lock' : 'dot'} />
        {state && <span className="visually-hidden">{state}</span>}
      </span>
      <span className="lesson-row__title">{lecture.title}</span>
      <div className="bar bar--slim" aria-hidden="true">
        <div className="bar__fill" style={{ width: `${pct}%` }} />
      </div>
      <span className="lesson-row__pct">{!lecture.completed && lecture.progress > 0 ? `${lecture.progress}%` : ''}</span>
    </>
  )

  // A shut lesson is rendered as plain text rather than a dead link: a link
  // that refuses to go anywhere is worse than something that never looked
  // clickable.
  if (shut) {
    return (
      <div className="lesson-row lesson-row--shut" title={state}>
        {inner}
      </div>
    )
  }
  return (
    <Link className="lesson-row" to={`/lessons/${lecture.id}`}>
      {inner}
    </Link>
  )
}

// Drawn rather than typed: an emoji looks different on every platform, and a
// lock glyph that renders as a coloured picture would be the only one on the page.
const GLYPHS = {
  check: <path d="M9.6 16.2 5.4 12l-1.4 1.4 5.6 5.6 12-12L20.2 5.6z" />,
  dot: <circle cx="12" cy="12" r="4.5" />,
  lock: (
    <path d="M17 9h-1V7a4 4 0 0 0-8 0v2H7a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2zm-7-2a2 2 0 0 1 4 0v2h-4V7z" />
  ),
}

function Glyph({ name }: { name: keyof typeof GLYPHS }) {
  return (
    <svg className="glyph" viewBox="0 0 24 24" aria-hidden="true">
      {GLYPHS[name]}
    </svg>
  )
}
